<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import {
    focalFromPointer,
    nudgeFocal,
    phoneCropRect,
    phonePlateRect,
  } from '@/domain/design/focal-point';
  import type { IFocalPoint } from '@/interfaces/IStorefrontStyle';

  /**
   * The banner's focal point (Storefront V2 ADM-3): the photo at its natural
   * aspect, the part a phone cover shows outlined with the logo in its corner
   * (E110), and a handle at the point that must always show. Mouse and pen set
   * the point and drag it; a touch sets it only on a tap, so a swipe keeps
   * scrolling the page; the arrow keys move it 5 % from the focused handle.
   */
  const props = defineProps<{
    src: string;
    value: IFocalPoint;
    /** The store's logo, shown where it covers the photo (a blank plate without one). */
    logo?: string;
  }>();
  const emit = defineEmits<{ (e: 'input', focal: IFocalPoint): void }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  /** A touch that moved at most this far is a tap. */
  const TAP_SLOP_PX = 8;

  const stage = ref<HTMLElement | null>(null);
  const natural = ref<{ width: number; height: number } | null>(null);
  const failed = ref(false);
  const logoFailed = ref(false);
  let gesture: {
    pointerId: number;
    touch: boolean;
    startX: number;
    startY: number;
  } | null = null;

  // Another logo: it may load this time.
  watch(
    () => props.logo,
    () => {
      logoFailed.value = false;
    }
  );

  // Another photo: its size and load state are unknown again.
  watch(
    () => props.src,
    () => {
      natural.value = null;
      failed.value = false;
      gesture = null;
    }
  );

  const cropStyle = computed(() => {
    const size = natural.value;
    const rect = size
      ? phoneCropRect(size.width, size.height, props.value)
      : null;
    return rect
      ? {
          left: `${rect.left}%`,
          top: `${rect.top}%`,
          width: `${rect.width}%`,
          height: `${rect.height}%`,
        }
      : null;
  });
  /** The logo plate in the outline's corner: percentages of the image, like the outline. */
  const plateStyle = computed(() => {
    const size = natural.value;
    const rect = size
      ? phonePlateRect(size.width, size.height, props.value)
      : null;
    return rect
      ? {
          left: `${rect.left}%`,
          top: `${rect.top}%`,
          width: `${rect.width}%`,
          height: `${rect.height}%`,
        }
      : null;
  });
  const handleStyle = computed(() => ({
    left: `${props.value.x}%`,
    top: `${props.value.y}%`,
  }));
  const handleLabel = computed(() =>
    t('design.cover.focalLabel', { x: props.value.x, y: props.value.y })
  );

  function onLoad(event: Event) {
    const image = event.target;
    if (image instanceof HTMLImageElement)
      natural.value = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
  }

  function onError() {
    failed.value = true;
  }

  /** A logo that does not load leaves the plain plate. */
  function onLogoError() {
    logoFailed.value = true;
  }

  function pointAt(clientX: number, clientY: number) {
    if (!stage.value) return;
    const next = focalFromPointer(
      stage.value.getBoundingClientRect(),
      clientX,
      clientY
    );
    if (next.x !== props.value.x || next.y !== props.value.y)
      emit('input', next);
  }

  function onPointerDown(event: PointerEvent) {
    if (!event.isPrimary) return;
    const touch = event.pointerType === 'touch';
    if (!touch && event.button !== 0) return;
    gesture = {
      pointerId: event.pointerId,
      touch,
      startX: event.clientX,
      startY: event.clientY,
    };
    if (touch) return;
    stage.value?.setPointerCapture(event.pointerId);
    pointAt(event.clientX, event.clientY);
  }

  function onPointerMove(event: PointerEvent) {
    if (!gesture || gesture.touch || event.pointerId !== gesture.pointerId)
      return;
    pointAt(event.clientX, event.clientY);
  }

  function onPointerUp(event: PointerEvent) {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const { touch, startX, startY } = gesture;
    gesture = null;
    const moved = Math.hypot(event.clientX - startX, event.clientY - startY);
    if (touch && moved <= TAP_SLOP_PX) pointAt(event.clientX, event.clientY);
  }

  /** The browser took the touch over (a scroll): no point is set. */
  function onPointerCancel(event: PointerEvent) {
    if (gesture && event.pointerId === gesture.pointerId) gesture = null;
  }

  function onKeydown(event: KeyboardEvent) {
    const next = nudgeFocal(props.value, event.key);
    if (!next) return;
    event.preventDefault();
    emit('input', next);
  }

  onBeforeUnmount(() => {
    gesture = null;
  });
</script>

<template>
  <div class="v2-design-focal-picker">
    <p v-if="failed" class="focal-error" role="alert">
      {{ t('design.cover.loadError') }}
    </p>
    <div
      v-else
      ref="stage"
      class="focal-stage"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
    >
      <img
        class="focal-image"
        :src="src"
        alt=""
        draggable="false"
        @load="onLoad"
        @error="onError"
      />
      <div
        v-if="cropStyle"
        class="focal-crop"
        :style="cropStyle"
        aria-hidden="true"
      />
      <div
        v-if="plateStyle"
        class="focal-plate"
        :style="plateStyle"
        aria-hidden="true"
      >
        <img
          v-if="logo && !logoFailed"
          class="plate-logo"
          :src="logo"
          alt=""
          draggable="false"
          @error="onLogoError"
        />
      </div>
      <button
        type="button"
        class="focal-handle"
        :style="handleStyle"
        :aria-label="handleLabel"
        @keydown="onKeydown"
      >
        <span class="handle-dot" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-design-focal-picker {
    .focal-error {
      @apply tw-m-0 tw-rounded-lg tw-bg-neutral-100 tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-neutral-700;
    }

    .focal-stage {
      @apply tw-relative tw-cursor-crosshair tw-select-none tw-overflow-hidden tw-rounded-lg tw-bg-neutral-100;
      touch-action: pan-y;
    }

    .focal-image {
      @apply tw-block tw-h-auto tw-w-full;
      -webkit-user-drag: none;
    }

    .focal-crop {
      @apply tw-pointer-events-none tw-absolute tw-rounded-sm;
      border: 2px solid rgb(var(--c-surface));
      box-shadow: 0 0 0 9999px rgb(var(--c-scrim) / 0.45);
    }

    /* The storefront's plate: opaque, its corners as round. */
    .focal-plate {
      @apply tw-pointer-events-none tw-absolute tw-overflow-hidden tw-border-2 tw-border-solid tw-border-surface tw-bg-surface;
      border-radius: 22%;

      .plate-logo {
        @apply tw-block tw-h-full tw-w-full tw-object-cover;
        -webkit-user-drag: none;
      }
    }

    .focal-handle {
      @apply tw-absolute tw-flex tw-h-11 tw-w-11 tw-cursor-grab tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0;
      margin: -22px 0 0 -22px;

      &:focus-visible {
        @apply tw-outline-none;

        .handle-dot {
          @apply tw-ring-4 tw-ring-orange-500/40;
        }
      }
    }

    .handle-dot {
      @apply tw-block tw-h-5 tw-w-5 tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-900 tw-bg-surface tw-shadow-sm;
    }
  }
</style>
